import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Menu, MenuItem } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import downloadIcon from '@/assets/images/Download.svg'
import duplicateIcon from '@/assets/images/Duplicate.svg'
import moveIcon from '@/assets/images/Move.svg'
import playIcon from '@/assets/images/PlayIcon.svg'
import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import {
  useDeleteCaseImageMutation,
  useDuplicateCaseImageMutation,
} from '@/store/apiService'
import { getProjectPermissions } from '@/utils/constants'
import { downloadImages } from '@/utils/helper'

import MoveImageDialog from './MoveImageDialog'
import StartTaskModal from './StartTaskModal'

interface ActionMenuProps {
  anchorEl: null | HTMLElement
  handleMenuClose: () => void
  caseId: string
  refetch: () => void
  imageId: string
  caseDetails: any
}

const ImageActionMenu: React.FC<ActionMenuProps> = ({
  anchorEl,
  handleMenuClose,
  caseId,
  refetch,
  imageId,
  caseDetails,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'ProjectDetails.ProjectDetailsActions',
  })

  const { t: translate } = useTranslation('translation', {
    keyPrefix: 'ProjectDetails.DeleteActionDialog',
  })

  const [duplicateCaseImage] = useDuplicateCaseImageMutation()
  const [deleteCaseImage] = useDeleteCaseImageMutation()

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isMoveDialog, setIsMoveDialog] = useState(false)
  const [isStartTaskMenu, setIsStartTaskMenu] = useState(false)
  const [selectedImageType, setSelectedImageType] = useState<
    string | undefined
  >()

  const permissions = getProjectPermissions(caseDetails?.role)

  const handleDownload = async () => {
    const image = caseDetails?.images?.find(
      (img: { id: string }) => img.id === imageId,
    )
    if (!image) return

    const names =
      image.type === 'raw'
        ? image.key_raw.split('/')
        : image.key_image.split('/')
    const fileName = names.length > 0 ? names[names.length - 1] : image.name

    const formattedImage = [
      {
        name: fileName,
        url: image.type === 'raw' ? image.raw_image : image.image,
      },
    ]

    try {
      await downloadImages(formattedImage)
      handleMenuClose()
      toast.success(t('actionToast.imageDownloaded'))
    } catch (err) {
      toast.error(t('actionToast.imageDownloadFailed'))
    }
  }

  const handleDuplicate = async () => {
    handleMenuClose()
    if (!caseId || !caseDetails?.images) return

    const originalImageCount = caseDetails.images.length

    try {
      await duplicateCaseImage({
        ids: [imageId],
        caseId: caseId,
      }).unwrap()

      const pollInterval = 2000
      const maxAttempts = 10
      let attempts = 0

      const pollForImage = async () => {
        try {
          const updated = await refetch()
          const updatedImages = updated?.data?.images || []
          if (updatedImages.length > originalImageCount) {
            toast.success(t('actionToast.imageDuplicated'))
            return
          }

          if (++attempts < maxAttempts) {
            setTimeout(pollForImage, pollInterval)
          } else {
            toast.error('Image duplicated, but update not detected in time.')
          }
        } catch (err) {
          toast.error('Polling failed.')
        }
      }

      pollForImage()
    } catch (err) {
      toast.error(t('actionToast.imageDuplicateFailed'))
    }
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'rgba(41, 41, 41, 1)',
              color: 'white',
              mt: 1,
              boxShadow:
                '0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.20)',
              borderRadius: '4px',
            },
          },
          list: {
            sx: {
              bgcolor: 'rgba(41, 41, 41, 1)',
            },
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {permissions?.canStartTask && (
          <MenuItem
            onClick={() => {
              const image = caseDetails?.images?.find(
                (img: { id: string }) => img.id === imageId,
              )
              setSelectedImageType(image?.type)
              setIsStartTaskMenu(true)
            }}
            sx={{ '&:hover': { backgroundColor: '#33393E' } }}
          >
            <Box
              component="img"
              src={playIcon}
              alt="Start Task"
              sx={{ mr: '12px' }}
            />
            {t('startTask')}
          </MenuItem>
        )}

        {permissions?.canDownload && (
          <MenuItem
            onClick={handleDownload}
            sx={{ '&:hover': { backgroundColor: '#33393E' } }}
          >
            <Box
              component="img"
              src={downloadIcon}
              alt="Download"
              sx={{ mr: '12px' }}
            />
            {t('download')}
          </MenuItem>
        )}

        {permissions?.canDuplicate && (
          <MenuItem
            onClick={handleDuplicate}
            sx={{ '&:hover': { backgroundColor: '#33393E' } }}
          >
            <Box
              component="img"
              src={duplicateIcon}
              alt="Duplicate"
              sx={{ mr: '12px' }}
            />
            {t('duplicate')}
          </MenuItem>
        )}

        {permissions?.canMoveImages && (
          <MenuItem
            onClick={() => {
              setIsMoveDialog(true)
            }}
            sx={{ '&:hover': { backgroundColor: '#33393E' } }}
          >
            <Box
              component="img"
              src={moveIcon}
              alt="Move"
              sx={{ mr: '12px' }}
            />
            {t('moveImage')}
          </MenuItem>
        )}

        {permissions?.canDeleteImages && (
          <MenuItem
            onClick={() => {
              setIsDeleteDialogOpen(true)
            }}
            sx={{ '&:hover': { backgroundColor: '#33393E' } }}
          >
            <DeleteIcon sx={{ mr: '12px' }} />
            {t('delete')}
          </MenuItem>
        )}
      </Menu>

      {/* Dialogs */}
      <Dialog
        triggerElement={null}
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title={translate('title')}
        content={
          <p>
            {translate('description')}
            <br />
            {translate('action')}
          </p>
        }
        paperStyles={{ width: '600px' }}
        actionButtons={
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              label={translate('cancelButton')}
              onClick={() => {
                setIsDeleteDialogOpen(false)
                handleMenuClose()
              }}
              textTransform="uppercase"
            />
            <Button
              variant="contained"
              label={translate('deleteButton')}
              onClick={async () => {
                if (caseId) {
                  try {
                    await deleteCaseImage({
                      ids: [imageId],
                      caseId: caseId,
                    }).unwrap()
                    setIsDeleteDialogOpen(false)
                    toast.success(translate('imageDeleted'))
                    refetch()
                  } catch (err) {
                    toast.error(translate('imageDeleteFailed'))
                  }
                }
              }}
              textTransform="uppercase"
            />
          </Box>
        }
      />

      {isMoveDialog && (
        <MoveImageDialog
          open={isMoveDialog}
          onClose={() => setIsMoveDialog(false)}
          caseId={caseId}
          refetchRecipe={refetch}
          imageId={imageId}
        />
      )}

      {isStartTaskMenu && (
        <StartTaskModal
          open={isStartTaskMenu}
          onClose={() => setIsStartTaskMenu(false)}
          imageId={imageId}
          imageTypes={selectedImageType}
          caseId={caseId}
        />
      )}
    </>
  )
}

export default ImageActionMenu
